import API, { commonApiInvoker } from "@api/api"
import Decimal from 'decimal.js'
import axios from 'axios'
import JSZip from 'jszip';
import { saveAs } from 'file-saver'


/**
 * 返回在容器内图片可以显示的最大宽高
 * @param {number} imgWidth - 图片宽度
 * @param {number} imgHeight - 图片高度
 * @param {number} containerWidth - 容器宽度
 * @param {number} containerHeight - 容器高度
 * @returns 
 */
const getImgMaxSizeInContainer = (imgWidth, imgHeight, containerWidth, containerHeight) => {
    if (!containerWidth || !containerHeight) return { width: 0, height: 0 }
    if (!imgWidth || !imgHeight) return { width: 0, height: 0 }
    const containerAspectRatio = Decimal.div(containerWidth, containerHeight)
    const imageAspectRatio = Decimal.div(imgWidth, imgHeight)
    let maxWidth, maxHeight;
    if (containerAspectRatio.greaterThan(imageAspectRatio)) {
        // 容器的宽高比较大，以容器的高度为基准计算最大宽度
        maxHeight = containerHeight;
        maxWidth = Decimal.mul(maxHeight, imageAspectRatio).toNumber();
    } else {
        // 图片的宽高比较大，以容器的宽度为基准计算最大高度
        maxWidth = containerWidth;
        maxHeight = Decimal.div(maxWidth, imageAspectRatio).toNumber();
    }

    return { width: maxWidth, height: maxHeight }
}

// 将图片url转成base64
async function urlToBase64(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.readAsDataURL(blob);
    });
}

// 获取图片宽高
function getImageDimensions(base64Image) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        let objectUrl = null

        img.onload = function () {
            const { width, height } = this

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }

            resolve({ width, height })
        }

        img.onerror = function () {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
            reject(new Error('Failed to load image.'))
        }

        if (base64Image instanceof File) {
            objectUrl = URL.createObjectURL(base64Image)
            img.src = objectUrl
        } else {
            img.src = base64Image
        }
    })
}

function resizeImage(base64, width, height) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            const resizedBase64 = canvas.toDataURL('image/png');

            resolve(resizedBase64);
        };
        img.onerror = reject;
        img.src = base64;
    });
}


// 将所有带透明度的白色转化为不带透明度
function convertTransparentWhiteToOpaque(base64Image) {
    return new Promise((resolve, reject) => {
        let img = new Image();
        img.src = base64Image;

        img.onload = () => {
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            let data = imageData.data;

            // 记录变化的像素数量，用于调试
            let changedPixels = 0;

            for (let i = 0; i < data.length; i += 4) {
                let r = data[i];
                let g = data[i + 1];
                let b = data[i + 2];
                let a = data[i + 3];

                // 如果像素不是黑色
                if (r !== 0 || g !== 0 || b !== 0) {
                    data[i] = 255;     // 设置 R 为 255
                    data[i + 1] = 255; // 设置 G 为 255
                    data[i + 2] = 255; // 设置 B 为 255
                    data[i + 3] = 255; // 设置 A 为 255
                    changedPixels++;
                }
            }

            // 更新 canvas 上的图像数据
            ctx.putImageData(imageData, 0, 0);

            // 将 canvas 转换为 base64 格式
            let newBase64Image = canvas.toDataURL();

            // 输出调试信息
            console.log(`Changed ${changedPixels} pixels`);

            resolve(newBase64Image);
        };

        img.onerror = (error) => {
            reject(`Image load error: ${error}`);
        };
    });
}

// 将base64转 file 对象
function convertBase64ToFile(base64Image, fileName) {
    const base64 = (base64Image.split(',')[1] || '')
        .replace(/[\r\n\s]/g, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const byteArray = atob(base64);
    const arrayBuffer = new ArrayBuffer(byteArray.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteArray.length; i++) {
        uint8Array[i] = byteArray.charCodeAt(i);
    }
    const blob = new Blob([arrayBuffer], { type: 'image/png' });
    const file = new File([blob], fileName, { type: 'image/png' });
    return file;
}

/**
 * 转换图片颜色
 * @param {*} base64 
 * @param  {Color[]} colors - 转换的颜色数组，以两个为一对进行转换 
 */
const convertImgColor = (base64, ...colors) => {
    // console.time('=========convertImgColor==========')
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous'
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                const red = data[i];
                const green = data[i + 1];
                const blue = data[i + 2];
                const alpha = data[i + 3];

                // console.log('color？', red, green, blue, alpha)
                // 判断像素颜色是否与原始颜色相等
                for (let colorGroup of colors) {
                    if (colorGroup.length != 2) return reject(new Error('参数错误'))
                    const [fromColor, toColor] = colorGroup
                    if (fromColor.length < 3 || toColor.length < 3) return reject(new Error('参数错误'))
                    if (
                        (red == fromColor[0] || fromColor[0] == '*') &&
                        (green == fromColor[1] || fromColor[1] == '*') &&
                        (blue == fromColor[2] || fromColor[2] == '*') &&
                        (fromColor.length < 4 || (fromColor.length > 3 && (alpha == fromColor[3] || fromColor[3] == '*')))
                    ) {
                        // 修改像素颜色为新颜色
                        data[i] = toColor[0];
                        data[i + 1] = toColor[1];
                        data[i + 2] = toColor[2];
                        if (fromColor.length > 3) data[i + 3] = toColor[3];
                        break // 每组像素点仅改变一次，否则会导致重复覆盖
                    }
                }
            }

            ctx.putImageData(imageData, 0, 0);

            // console.timeEnd('=========convertImgColor==========')

            resolve(canvas.toDataURL());
        }
        image.onerror = reject
        image.src = base64;
    })
}

function convertTransparentToBlackNonTransparentToWhite(base64Image) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = base64Image;

        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = img.width;
            canvas.height = img.height;

            // 将图像绘制到 canvas 上
            ctx.drawImage(img, 0, 0);

            // 获取图像的像素数据
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // 修改像素数据
            for (let i = 0; i < data.length; i += 4) {
                const alpha = data[i + 3]; // alpha 通道值

                if (alpha === 0) {
                    // 透明像素，改为黑色
                    data[i] = 0;     // R
                    data[i + 1] = 0; // G
                    data[i + 2] = 0; // B
                    data[i + 3] = 255; // A (不透明)
                } else {
                    // 非透明像素，改为白色
                    data[i] = 255;     // R
                    data[i + 1] = 255; // G
                    data[i + 2] = 255; // B
                    data[i + 3] = 255; // A (保持不透明)
                }
            }

            // 将修改后的像素数据放回 canvas
            ctx.putImageData(imageData, 0, 0);

            // 返回新的 base64 图片
            resolve(canvas.toDataURL("image/png"));
        };

        img.onerror = (error) => reject(error);
    });
}


// 通过fabric加载url
function loadImageUrlByFabric(url) {
    return new Promise(resolve => {
        fabric.Image.fromURL(url, (img) => {
            resolve(img)
        }, { crossOrigin: 'anonymous' })
    })
}



function personCheckFun(imageUrl) {
    // return commonApiInvoker(API.personCheck({
    //     imageUrl: imageUrl
    // })).then(res => {
    //     return res
    // })
}

function downloadFile(url, filename) {
    return axios({
        method: 'get',
        url: url,
        responseType: 'blob'
    }).then(res => {
        // console.log(fileUrl);
        const fileName = url.split('?')[0].split('/').pop();
        const fileExtension = fileName.split('.').pop(); // 获取文件类型后缀

        const blob = new Blob([res.data], {
            type: `application/octet-stream.${fileExtension}` // 添加文件类型后缀
        });

        let link = document.createElement('a');
        let body = document.querySelector('body');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || 'download';
        link.style.display = 'none';
        body.appendChild(link);
        link.click();
        body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    });
}

// 解析 Base64 是否包含透明通道
function hasAlphaChannel(base64String) {
    return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous'; // 设置跨域属性，避免污染 canvas
        img.onload = function () {
            var canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            var context = canvas.getContext('2d');
            context.drawImage(img, 0, 0);

            var imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            var data = imageData.data;

            for (var i = 3; i < data.length; i += 4) {
                if (data[i] < 255) {
                    resolve(true); // 存在透明通道
                    return;
                }
            }
            resolve(false); // 不存在透明通道
        };
        img.onerror = function () {
            reject(new Error('Failed to load image'));
        };
        img.src = base64String;
    });
}

const parseErrorCode = (errorStr) => {
    const DEFAULT_ERROR_MESSAGE = '图片不合格，请重试'
    const ERROR_MESSAGE = {
        '71000': '点不能为空，请拖拽位置',
        '71001': '区域不能为空，请锁定区域',
        '71002': '资源错误，请刷新页面',
        '71003': '资源错误，请刷新页面',
        '71004': '图片不合格，请重试',
        '71005': '任务排队中，请稍后重试',
        '71006': '资源错误，请刷新页面',
        '71007': '图片不合格，请重试',
        '71008': '请选择预置素材库',
        '71009': '图片资源获取失败，请稍后重试',
        '71010': '资源错误，请刷新页面',
        '71011': '资源错误，请刷新页面',
        '71105': 'AI试衣不支持',
        '71106': '模特图不合格',
        '72001': '商品类型不支持，请更换服装图',
        '422': '请求参数错误，请刷新重试',
        '502': '服务繁忙，请稍后重试',
        '503': '服务繁忙，请稍后重试',
        '-61201': '服务繁忙，请稍后重试',
        '61449': '图片不合格，请重试',
    }
    const res = /\[(-?\d+)\]|(-?\d+)/.exec(errorStr)
    const code = res ? res[1] || res[2] : '502'
    const message = ERROR_MESSAGE[code] ? ERROR_MESSAGE[code] : DEFAULT_ERROR_MESSAGE
    return {
        code: code,
        message: code ? `[${code}] ${message}` : message
    }
}


function getImagesDimensions(urls) {
    const result = []; // 存储最终结果的数组

    return new Promise((resolve, reject) => {
        let completed = 0;

        urls.forEach((url) => {
            const img = new Image();
            img.src = url;

            img.onload = () => {
                // 图片加载成功后获取宽高
                let width = Math.floor(img.naturalWidth / 8) * 8; // 向下取8的整数倍
                let height = Math.floor(img.naturalHeight / 8) * 8;

                // 将结果添加到结果数组
                result.push({
                    inputImage: url,
                    inputParams: {
                        left: 0,
                        top: 0,
                        right: width,
                        bottom: height,
                    },
                });

                completed++;
                // 当所有图片处理完成后，resolve 结果
                if (completed === urls.length) {
                    resolve(result);
                }
            };

            img.onerror = () => {
                console.error(`无法加载图片：${url}`);
                completed++;
                if (completed === urls.length) {
                    resolve(result); // 忽略错误，继续返回已有结果
                }
            };
        });
    });
}

// 将阿里云OSS图片转为webp格式显示
// function getImgUrlWithWebp(url, resize = false, blur = false) {
//   if (!url) {
//     return url;
//   }
//   let suffix = 'x-oss-process=image'
//   if (resize) suffix += '/resize,w_400/quality,q_90'
//   if (blur) suffix += '/blur,r_4,s_4'
//   suffix += '/format,webp'
//   if (url.indexOf('?') >= 0) {
//     url = url + '&' + suffix;
//   } else {
//     url = url + '?' + suffix;
//   }
//   return url;
// }

const errList = {
    '76006': 'source图中无法检测到人脸',
    '76007': 'FRT的输入形式不对',
    '76008': 'FRT的控制参数错误',
};

/**
 * 下载图片列表并打包成压缩文件
 * @param {Array} list - 包含 url 列表、sessionId、status 和 errcode 的数组
 * @param {String} folderName - 一级文件夹名称
 * @returns {Promise<Boolean>} - 返回一个 Promise，在压缩完成后返回 true
 */
async function downloadImagesAndZip(list, folderName) {
    const zip = new JSZip(); // 创建 JSZip 实例
    const folder = zip.folder(folderName); // 创建一级文件夹
    let errorLog = ''; // 用于记录失败的 sessionId 和 errcode

    for (let index = 0; index < list.length; index++) {
        const { output, sessionId, status, errcode, inputImage } = list[index];

        // 下载 inputImage 并存入文件夹
        if (inputImage) {
            const inputFileName = `${index}_源素材${getExtension(inputImage)}`;
            await downloadAndAddToZip(inputImage, folder, inputFileName, (err) => {
                console.error(`Error downloading inputImage ${inputImage}:`, err);
                errorLog += `${sessionId} - 原素材下载失败: ${inputImage}\n`;
            });
        }

        // 原有逻辑：根据 status 判断是否成功
        if (status === 'succeeded' && Array.isArray(output)) {
            for (let i = 0; i < output.length; i++) {
                const url = output[i];
                const outputFileName =
                    output.length === 1
                        ? `${index}_结果图${getExtension(url)}`
                        : `${index}_结果图_${i + 1}${getExtension(url)}`;
                await downloadAndAddToZip(url, folder, outputFileName, (err) => {
                    console.error(`Error downloading ${url}:`, err);
                    errorLog += `${sessionId} - 输出图片下载失败: ${url}\n`;
                });
            }
        } else if (status === 'failed') {
            errorLog += `${sessionId}: ${errList[errcode] || errcode}\n`;
        }
    }

    // 如果有错误日志，添加到 zip 的一级目录中
    if (errorLog) {
        folder.file('error_log.txt', errorLog);
    }

    // 生成压缩包并触发下载
    try {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `${folderName}.zip`); // 使用 FileSaver 保存文件
        return true;
    } catch (error) {
        console.error('Error generating zip:', error);
        return false;
    }
}

/**
 * 下载图片并添加到 ZIP 文件夹
 * @param {String} url - 图片的 URL
 * @param {Object} zipFolder - ZIP 文件夹对象
 * @param {String} fileName - 文件名
 * @param {Function} errorCallback - 出错时调用的回调函数
 */
async function downloadAndAddToZip(url, zipFolder, fileName, errorCallback) {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP status ${response.status}`);
        }
        const blob = await response.blob();
        zipFolder.file(fileName, blob);
    } catch (error) {
        if (errorCallback) errorCallback(error);
    }
}

/**
 * 根据 URL 获取文件扩展名
 * @param {String} url - 图片的 URL
 * @returns {String} 文件扩展名
 */
function getExtension(url) {
    const match = url.match(/\.\w+(?=\?|$)/);
    return match ? match[0] : '.jpg'; // 默认扩展名为 .jpg
}

// 利用阿里云resize最大边
async function resizeImageUrlMaxByAliyun(imageUrl, max) {
    try {
        if (typeof imageUrl !== 'string' || !imageUrl?.trim()) {
            throw new Error('Invalid URL');
        }

        // 获取图片的原始宽高
        const imgSize = await getImageDimensionsFromUrl(imageUrl);
        const originalWidth = imgSize.width;
        const originalHeight = imgSize.height;

        let width = originalWidth;
        let height = originalHeight;
        let useOrigin = true;

        // 检查是否需要调整尺寸
        if (Math.max(originalWidth, originalHeight) > max) {
            useOrigin = false;
            const aspectRatio = originalWidth / originalHeight;
            if (originalWidth > originalHeight) {
                width = max;
                height = Math.floor(width / aspectRatio);
            } else {
                height = max;
                width = Math.floor(height * aspectRatio);
            }
        }

        if (useOrigin) {
            return {
                success: true,
                url: imageUrl,
                originalWidth,
                originalHeight,
                resizedWidth: originalWidth,
                resizedHeight: originalHeight
            };
        } else {
            // 通过阿里云OSS调整图片尺寸
            const resizedUrl = addParamsToUrl(imageUrl, { "x-oss-process": `image/resize,w_${width},h_${height},m_fixed` })

            return {
                success: true,
                url: resizedUrl,
                originalWidth,
                originalHeight,
                resizedWidth: width,
                resizedHeight: height
            };
        }
    } catch (err) {
        return {
            success: false,
            message: err.message
        };
    }
}

async function getImageDimensionsFromUrl(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => reject(new Error('Failed to load image dimensions'));
        img.src = url;
    });
}

async function resizeBase64Image(base64, width, height) {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = function () {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                const resizedBase64 = canvas.toDataURL('image/png');
                resolve(resizedBase64);
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = function () {
            reject(new Error("Invalid image or failed to load."));
        };

        img.src = base64;
    });
}


// 下载交付图
const fetchImageBlob = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${url}`);
        return await response.blob();
    } catch (error) {
        console.error("Image download error:", error);
        return null; // 失败时返回 null
    }
};

// 获取 URL 文件名后缀
const getFileExtension = (url) => {
    const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1] : "jpg"; // 默认 jpg
};

const getFileNameFromUrl = (url) => {
    const match = url.match(/\/([^\/?]+)(?:\?|$)/);
    return match ? match[1] : "image";
};

const MAX_ZIP_SIZE = 500 * 1024 * 1024;

const createNewZip = () => {
    const zip = new JSZip();
    return {
        zip,
        mainFolder: zip,
        size: 0
    };
};

const saveCurrentZip = async (zip, index) => {
    const timestamp = Date.now();
    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `${timestamp}_DeliveryImages_Part${index}.zip`);
};

// 非寄拍商品下载
const downloadImages2 = async (selectedRows, downloadTypes = ['origin', 'match', 'reference', 'delivery']) => {
    if (!selectedRows || selectedRows.length === 0) return false;

    let zipIndex = 1;
    let currentZip = createNewZip();
    let currentSize = 0;
    let currentFolder = null;

    const processFile = async (url, fileName, folderName) => {
        const blob = await fetchImageBlob(url);
        if (!blob) return false;

        if (!currentFolder || currentFolder.name !== folderName) {
            currentFolder = currentZip.mainFolder.folder(folderName);
        }

        if (currentSize + blob.size > MAX_ZIP_SIZE) {
            await saveCurrentZip(currentZip.zip, zipIndex);
            zipIndex++;
            currentZip = createNewZip();
            currentSize = 0;
            currentFolder = currentZip.mainFolder.folder(folderName);
        }

        currentFolder.file(fileName, blob);
        currentSize += blob.size;
        return true;
    };

    for (const row of selectedRows) {
        const folderName = row.skuCode || row.skuNumber || "unknown";



        // 下载 allImages (origin)
        if (downloadTypes.includes('match') && row.allImageMap?.length > 0) {
            for (const item of row.allImageMap) {
                const url = item.url;
                const ext = getFileExtension(url);
                const fileName = `${item.name}.${ext}`;
                await processFile(url, fileName, folderName)
            }
        }

        // 下载 matchImageMap (match) - 保持不变
        if (downloadTypes.includes('match') && row.matchImageMap?.length > 0) {
            for (const item of row.matchImageMap) {
                const url = item.url;
                const ext = getFileExtension(url);
                const fileName = `${item.name}.${ext}`;
                await processFile(url, fileName, folderName)
            }
        }

        // 下载 refImages (reference) - 修改后的逻辑
        if (downloadTypes.includes('reference')) {
            const seenMd5 = new Set(); // 用于refImage去重

            if (row.deliveryImages?.length > 0) {
                // 下载deliveryImages中的refImage
                let refImageCount = 0;
                const seenUrls = new Set(); // 新增URL去重集合
                for (const item of row.deliveryImages) {
                    if (!item.refImage) continue;

                    // 根据md5去重
                    const md5Match = item.refImage.match(/\?md5=([^&]+)/);
                    const md5 = md5Match ? md5Match[1] : null;

                    // 新增URL去重检查
                    if (seenUrls.has(item.refImage)) continue;
                    seenUrls.add(item.refImage);

                    if (md5 && seenMd5.has(md5)) continue;
                    if (md5) seenMd5.add(md5);

                    refImageCount++;
                    const ext = getFileExtension(item.refImage);
                    const fileName = `参考图${refImageCount}.${ext}`;
                    await processFile(item.refImage, fileName, folderName)
                }
            } else if (row.refImages?.length > 0) {
                // 保持原有refImages逻辑，增加md5去重
                const seenMd5 = new Set();
                let refImageCount = 0;

                for (let i = 0; i < row.refImages.length; i++) {
                    const url = row.refImages[i];

                    // 提取md5参数
                    const md5Match = url.match(/\?md5=([^&]+)/);
                    const md5 = md5Match ? md5Match[1] : null;

                    // 如果md5已存在则跳过
                    if (md5 && seenMd5.has(md5)) continue;
                    if (md5) seenMd5.add(md5);

                    const ext = getFileExtension(url);
                    refImageCount++;
                    const fileName = `参考图${refImageCount}.${ext}`;
                    await processFile(url, fileName, folderName)
                }
            }
        }

        // 下载 deliveryImages (delivery) - 保持不变
        if (downloadTypes.includes('delivery') && row.deliveryImages?.length > 0) {
            const prefix = row.skuCode ? row.skuCode : row.skuNumber
            for (let i = 0; i < row.deliveryImages.length; i++) {
                const item = row.deliveryImages[i];
                const url = item.url;
                const ext = getFileExtension(url);
                let fileName = `${prefix}-${i + 1}.${ext}`;
                if (item.status === 3) fileName = `不通过_${fileName}`;
                await processFile(url, fileName, folderName)
            }
        }
    }

    if (currentSize > 0) {
        await saveCurrentZip(currentZip.zip, zipIndex);
    } else if (zipIndex === 1) {
        console.error("No valid files to download.");
        return false;
    }

    return true;
};

// 寄拍商品下载
const downloadImages = async (selectedRows, downloadTypes = ['origin', 'match', 'reference', 'delivery']) => {
    if (!selectedRows || selectedRows.length === 0) return false;

    let zipIndex = 1;
    let currentZip = createNewZip();
    let currentSize = 0;
    let currentFolder = null;

    const processFile = async (url, fileName, folderName) => {
        const blob = await fetchImageBlob(url);
        if (!blob) return false;

        if (!currentFolder || currentFolder.name !== folderName) {
            currentFolder = currentZip.mainFolder.folder(folderName);
        }

        if (currentSize + blob.size > MAX_ZIP_SIZE) {
            await saveCurrentZip(currentZip.zip, zipIndex);
            zipIndex++;
            currentZip = createNewZip();
            currentSize = 0;
            currentFolder = currentZip.mainFolder.folder(folderName);
        }

        currentFolder.file(fileName, blob);
        currentSize += blob.size;
        return true;
    };


    for (const row of selectedRows) {
        const folderName = row.skuCode || row.skuNumber || "unknown";



        // 处理origin类型图片
        if (downloadTypes.includes('origin')) {
            if (row.deliveryImages?.length > 0 && row.selectedImages?.length > 0) {
                // 下载selectedImages，使用URL中的文件名
                for (const url of row.selectedImages) {
                    const ext = getFileExtension(url);
                    const baseName = getFileNameFromUrl(url);
                    const fileName = `${baseName}.${ext}`;
                    await processFile(url, fileName, folderName)
                }
            }
        }

        // 下载 matchImageMap (match)
        if (downloadTypes.includes('match') && row.matchImageMap?.length > 0) {
            for (const item of row.matchImageMap) {
                const url = item.url;
                const ext = getFileExtension(url);
                const fileName = `${item.name}.${ext}`;
                await processFile(url, fileName, folderName)
            }
        }

        // 下载 refImages (reference)
        if (downloadTypes.includes('reference') && row.refImages?.length > 0) {
            const seenMd5 = new Set(); // 用于存储已处理的md5值
            let refImageCount = 0; // 计数器用于文件名编号

            for (let i = 0; i < row.refImages.length; i++) {
                const url = row.refImages[i];

                // 提取md5参数
                const md5Match = url.match(/\?md5=([^&]+)/);
                const md5 = md5Match ? md5Match[1] : null;

                // 如果md5已存在则跳过
                if (md5 && seenMd5.has(md5)) continue;
                if (md5) seenMd5.add(md5);

                const ext = getFileExtension(url);
                refImageCount++;
                const fileName = `参考图${refImageCount}.${ext}`;
                await processFile(url, fileName, folderName)
            }
        }

        // 下载 deliveryImages (delivery)
        if (downloadTypes.includes('delivery') && row.deliveryImages?.length > 0) {
            const prefix = row.skuCode ? row.skuCode : row.skuNumber
            for (let i = 0; i < row.deliveryImages.length; i++) {
                const item = row.deliveryImages[i];
                const url = item.url;
                const ext = getFileExtension(url);
                let fileName = `${prefix}-${i + 1}.${ext}`;
                if (item.status === 3) fileName = `不通过_${fileName}`;
                await processFile(url, fileName, folderName)
            }
        }
    }

    if (currentSize > 0) {
        await saveCurrentZip(currentZip.zip, zipIndex);
    } else if (zipIndex === 1) {
        console.error("No valid files to download.");
        return false;
    }

    return true;
};







function setOpaquePixels(base64Image) {
    // console.time('=========setOpaquePixels==========')
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < imageData.data.length; i += 4) {
                imageData.data[i] = 255;
                imageData.data[i + 1] = 255;
                imageData.data[i + 2] = 255;
                imageData.data[i + 3] = imageData.data[i + 3] / 204 * 255;
            }

            ctx.putImageData(imageData, 0, 0);
            const modifiedBase64 = canvas.toDataURL('image/png');

            // console.timeEnd('=========setOpaquePixels==========')
            resolve(modifiedBase64);
        };

        img.onerror = () => {
            reject(Error('Failed to load the image.'));
        };

        img.src = base64Image;
    });
}

// 原图加mask去除前景
function getForegroundImage(samOrign_1024, samClickMask) {
    // console.time('=========getForegroundImage==========')
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    function loadImage(src) {
        // console.time('=========loadImage==========')
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous"; // 允许跨域
            img.onload = () => {
                // console.timeEnd('=========loadImage==========')
                resolve(img)
            };
            img.onerror = reject;
            img.src = src;
        });
    }
    return new Promise((resolve, reject) => {
        Promise.all([
            loadImage(samOrign_1024),
            loadImage(samClickMask)
        ]).then(([originalImg, maskImg]) => {
            canvas.width = originalImg.width;
            canvas.height = originalImg.height;

            ctx.drawImage(originalImg, 0, 0);

            ctx.globalCompositeOperation = 'destination-in';

            ctx.drawImage(maskImg, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const maskData = ctx.getImageData(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < imageData.data.length; i += 4) {
                const a = maskData.data[i + 3];
                if (a < 0) {
                    imageData.data[i + 3] = 255; // 设置透明像素为不透明
                }
            }

            const base64 = canvas.toDataURL('image/png');

            // console.timeEnd('=========getForegroundImage==========')

            resolve(base64);
        });
    });
}

// 将黑白mask转为蓝色透明
function convertWhiteImageToBlue(base64Data) {
    // console.time('=========convertWhiteImageToBlue==========')
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;

            for (let i = 0; i < pixels.length; i += 4) {
                const red = pixels[i];
                const green = pixels[i + 1];
                const blue = pixels[i + 2];
                // Set non-black pixels to rgba(30, 144, 255, 0.8)
                pixels[i] = 30;
                pixels[i + 1] = 144;
                pixels[i + 2] = 255;
                pixels[i + 3] = red / 255 * 204;
            }

            ctx.putImageData(imageData, 0, 0);
            const convertedData = canvas.toDataURL();

            // console.timeEnd('=========convertWhiteImageToBlue==========')

            resolve(convertedData);
        };

        img.onerror = (error) => {
            reject(error);
        };
        img.src = base64Data;
    });
}

// 将蓝色透明转为黑白mask
function convertBlueImageToWhite(base64Data) {
    // console.time('=========convertWhiteImageToBlue==========')
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;

            for (let i = 0; i < pixels.length; i += 4) {
                const red = pixels[i];
                const green = pixels[i + 1];
                const blue = pixels[i + 2];
                const alpha = pixels[i + 3];
                // Set non-black pixels to rgba(30, 144, 255, 0.8)
                pixels[i] = alpha / 204 * 255;
                pixels[i + 1] = alpha / 204 * 255;
                pixels[i + 2] = alpha / 204 * 255;
                pixels[i + 3] = 255;
            }

            ctx.putImageData(imageData, 0, 0);
            const convertedData = canvas.toDataURL();

            // console.timeEnd('=========convertWhiteImageToBlue==========')

            resolve(convertedData);
        };

        img.onerror = reject;
        img.src = base64Data;
    });
}

// 为图片 URL 动态附加参数时
function addParamsToUrl(url, params) {
    try {
        const urlObj = new URL(url);

        // 合并已有参数与新参数
        const searchParams = new URLSearchParams(urlObj.search);
        Object.entries(params).forEach(([key, value]) => {
            searchParams.set(key, value);
        });

        urlObj.search = searchParams.toString();
        return urlObj.toString();
    } catch (e) {
        // 兼容相对路径 URL（如 '/images/logo.png'）
        return addParamsToUrl(new URL(url, window.location.href), params) // .pathname + urlObj.search;
    }
}

function removeImageExtension(fileName, accept = "image/png, image/jpeg, image/webp, image/avif") {
    if (!fileName || fileName == 'undefined') return '未命名';
    let allowedExtensions = accept.split(',')
        .map(type => type?.trim().split('/')[1])
        .filter(ext => ext);

    if (allowedExtensions.includes("jpeg") && !allowedExtensions.includes("jpg")) {
        allowedExtensions.push("jpg");
    }

    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex <= 0) return fileName;


    const fileExtension = fileName.slice(lastDotIndex + 1).toLowerCase();


    if (allowedExtensions.includes(fileExtension)) {
        return fileName.slice(0, lastDotIndex);
    }

    return fileName;
}

async function getImageLongEdge(url) {
    try {
        const dimensions = await getImageDimensions(url);
        return Math.max(dimensions.width, dimensions.height);
    } catch (error) {
        console.error('获取图片长边失败:', error);
        throw error;
    }
}

function downUrl(fileUrl, imageType, isHD) { //第二个参数是埋点需要

    function isBase64(src) {
        // 检查是否以 data: 开头
        return /^data:image\/[a-z]+;base64,/.test(src);
    }
    if (isBase64(fileUrl)) {
        let link = document.createElement('a')
        link.href = fileUrl
        link.download = `file_${new Date().getTime()}.png` // 自己制定下载文件的文件名
        // 兼容火狐浏览器
        link.style.display = 'none'	// 让这个a标签不可见
        document.body.appendChild(link)

        link.click()
        document.body.removeChild(link);
        return
    }
    return axios({
        method: 'get',
        url: fileUrl,
        responseType: 'blob'
    }).then(res => {
        // console.log(fileUrl);
        const fileName = fileUrl.split('?')[0].split('/').pop();
        const fileExtension = fileName.split('.').pop(); // 获取文件类型后缀

        const blob = new Blob([res.data], {
            type: `application/octet-stream.${fileExtension}` // 添加文件类型后缀
        });

        let link = document.createElement('a');
        let body = document.querySelector('body');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || 'download';
        link.style.display = 'none';
        body.appendChild(link);
        link.click();
        body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    });
}

/**
 * 从原图裁剪指定区域
 * @param {string} imageUrl - 原图 URL（未压缩的原图）
 * @param {number} offsetX - 裁剪区域左上角 x 坐标（相对原图）
 * @param {number} offsetY - 裁剪区域左上角 y 坐标（相对原图）
 * @param {number} width - 裁剪区域宽度（像素）
 * @param {number} height - 裁剪区域高度（像素）
 * @param {'png' | 'jpeg'} format - 可选，默认 png
 * @returns Promise<string> - 裁剪后的 base64 图片（data URL）
 */
async function cropImageByRegion({
    imageUrl,
    offsetX,
    offsetY,
    width,
    height,
    format = 'png',
    fileName = 'PSAI',
}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = async () => {
            const imgWidth = img.width;
            const imgHeight = img.height;

            // 自动纠错：贴边保护
            offsetX = Math.max(0, Math.round(offsetX));
            offsetY = Math.max(0, Math.round(offsetY));

            // 自动纠错：防止裁剪框越界
            if (offsetX + width > imgWidth) {
                width = imgWidth - offsetX;
            }
            if (offsetY + height > imgHeight) {
                height = imgHeight - offsetY;
            }

            try {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                ctx.drawImage(
                    img,
                    offsetX,
                    offsetY,
                    width,
                    height,
                    0,
                    0,
                    width,
                    height
                );

                const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
                const extension = format === 'jpeg' ? 'jpg' : 'png';
                const dataURL = canvas.toDataURL(mimeType, 0.92);
                // 转url
                let formdata = new FormData
                formdata.append('file', convertBase64ToFile(dataURL, format === 'jpeg' ? 'image.jpg' : 'image.png'))
                const res = await API.uploadImage(formdata)
                if (res.code != 0) throw new Error(res.message)
                // 取元信息图
                const metaRes = await API.generateMetadatav2({ image: res.data.filename })
                if (metaRes?.code != 0) throw new Error(metaRes?.message)
                downUrl(metaRes?.data?.[1])
                resolve(dataURL);
            } catch (err) {
                reject(err);
            }
        };

        img.onerror = () => {
            reject(new Error('图片加载失败，请检查 imageUrl 是否可访问'));
        };

        img.src = imageUrl;
    });
}

// 高清改尺寸-适合模式下载
async function exportImageContainMode(
    imgUrl,
    rectWidth,
    rectHeight,
    outputType = 'png',
    filename = 'PSAI'
) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous'; // 避免跨域污染
        img.onload = async () => {
            const originalWidth = img.width;
            const originalHeight = img.height;
            const imageRatio = originalWidth / originalHeight;
            const rectRatio = rectWidth / rectHeight;

            // contain 模式缩放（短边适配）
            let scale = 1;
            if (imageRatio > rectRatio) {
                scale = rectWidth / originalWidth;
            } else {
                scale = rectHeight / originalHeight;
            }

            const drawWidth = originalWidth * scale;
            const drawHeight = originalHeight * scale;
            const offsetX = (rectWidth - drawWidth) / 2;
            const offsetY = (rectHeight - drawHeight) / 2;

            // 创建离屏 canvas
            const canvas = document.createElement('canvas');
            canvas.width = rectWidth;
            canvas.height = rectHeight;
            const ctx = canvas.getContext('2d');

            // 处理背景
            if (outputType === 'jpeg') {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, rectWidth, rectHeight);
            } else {
                ctx.clearRect(0, 0, rectWidth, rectHeight); // 透明背景
            }

            // 绘制图片
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

            // 生成导出链接
            const mime = outputType === 'jpeg' ? 'image/jpeg' : 'image/png';
            const dataURL = canvas.toDataURL(mime, 0.92);
            // 转url
            let formdata = new FormData
            formdata.append('file', convertBase64ToFile(dataURL, outputType === 'jpeg' ? 'image.jpg' : 'image.png'))
            const res = await API.uploadImage(formdata)
            if (res.code != 0) throw new Error(res.message)
            // 取元信息图
            const metaRes = await API.generateMetadatav2({ image: res.data.filename })
            if (metaRes?.code != 0) throw new Error(metaRes?.message)
            downUrl(metaRes?.data?.[1])

            resolve(dataURL);
        };

        img.onerror = (e) => reject(e);
        img.src = imgUrl;
    });
}


// 上传base64图片
function uploadBase64Image(base64, isPng = true) {
    let formdata1 = new FormData
    formdata1.append('file', convertBase64ToFile(base64, isPng ? 'image.png' : 'image.jpg'))
    return API.uploadImage(formdata1).then(res => {
        if (res?.code != 0) throw new Error(res?.message)
        return res?.data?.filename
    }).catch(err => {
        console.error(err);
        return false;
    })
}





export default {
    getImgMaxSizeInContainer,
    urlToBase64,
    getImageDimensions,
    getForegroundImage,
    resizeImage,
    convertTransparentWhiteToOpaque,
    convertBase64ToFile,
    convertImgColor,
    loadImageUrlByFabric,
    personCheckFun,
    convertTransparentToBlackNonTransparentToWhite,
    downloadFile,
    hasAlphaChannel,
    parseErrorCode,
    getImagesDimensions,
    downloadImagesAndZip,
    resizeImageUrlMaxByAliyun,
    resizeBase64Image,
    downloadImages,
    downloadImages2,
    setOpaquePixels,
    convertWhiteImageToBlue,
    convertBlueImageToWhite,
    addParamsToUrl,
    removeImageExtension,
    getImageLongEdge,
    cropImageByRegion,
    exportImageContainMode,
    uploadBase64Image
}



